import { computed, reactive, type ComputedRef, type Ref } from 'vue';

import { getGenerator } from '../services/autoGenerateRegistry';
import type { FieldConfig } from '../types';

import { getFormData, getNestedValue } from './useFieldVisibility';

export interface AutoGenerateConfig {
    template?: string | null;
    generator?: string | null;
    watch: string[];
    params?: Record<string, unknown>;
}

interface AutoGenerateState {
    /** Whether the user has manually edited the field */
    isManuallyEdited: (key: string) => boolean;
    /** Mark a field as manually edited (disables auto-generation) */
    markManualEdit: (key: string) => void;
    /** Reset manual edit flag, re-enabling auto-generation */
    resetAutoGenerate: (key: string) => void;
    /** Check if a field has auto-generate config */
    hasAutoGenerate: (key: string) => boolean;
    /** Get the auto-generated value for a field (or null if dependencies missing) */
    getAutoGeneratedValue: (key: string) => string | null;
    /** Fields with auto-generate config */
    autoGenerateFields: ComputedRef<FieldConfig[]>;
}

// =========================================================================
// Template DSL Engine
// =========================================================================

type Transformer = (value: string) => string;

const transformers: Record<string, Transformer> = {
    lower: (v) => v.toLowerCase(),
    upper: (v) => v.toUpperCase(),
    first_char: (v) => v.charAt(0),
    slug: (v) =>
        v
            .toLowerCase()
            .trim()
            .replace(/[^\w\s-]/g, '')
            .replace(/[\s_]+/g, '-'),
    trim: (v) => v.trim(),
};

/**
 * Parse and apply a template string with field placeholders.
 *
 * Supported syntax:
 * - {field_key}                    → raw value
 * - {lower:field_key}              → apply "lower" transformer
 * - {lower|first_char:field_key}   → apply "first_char" then "lower" (left to right)
 */
function applyTemplate(
    template: string,
    fieldValues: Record<string, unknown>,
): string | null {
    let hasAllValues = true;

    const result = template.replace(
        /\{(?:([a-z_|]+):)?([^}]+)\}/g,
        (_match, transformerChain: string | undefined, fieldKey: string) => {
            const rawValue = getNestedValue(
                fieldValues as Record<string, unknown>,
                fieldKey,
            );

            if (rawValue === null || rawValue === undefined || rawValue === '') {
                hasAllValues = false;
                return '';
            }

            let value = String(rawValue);

            if (transformerChain) {
                const names = transformerChain.split('|');
                for (const name of names) {
                    const fn = transformers[name];
                    if (fn) {
                        value = fn(value);
                    }
                }
            }

            return value;
        },
    );

    return hasAllValues ? result : null;
}

// =========================================================================
// Composable
// =========================================================================

/**
 * Composable for auto-generating field values from other fields.
 *
 * @param fields - Reactive list of all field configs
 * @param formData - Reactive form data object (used to read source field values)
 */
export function useAutoGenerate(
    fields: Ref<FieldConfig[]> | ComputedRef<FieldConfig[]>,
    formData: Ref<Record<string, unknown>> | ComputedRef<Record<string, unknown>>,
): AutoGenerateState {
    /** Track which fields have been manually edited by the user */
    const manuallyEdited = reactive(new Set<string>());

    /** Fields that have auto-generate config */
    const autoGenerateFields = computed(() =>
        fields.value.filter((f) => f.autoGenerate),
    );

    function hasAutoGenerate(key: string): boolean {
        return autoGenerateFields.value.some((f) => f.key === key);
    }

    function isManuallyEdited(key: string): boolean {
        return manuallyEdited.has(key);
    }

    function markManualEdit(key: string): void {
        manuallyEdited.add(key);
    }

    function resetAutoGenerate(key: string): void {
        manuallyEdited.delete(key);
    }

    /**
     * Compute the auto-generated value for a field.
     * Returns null if dependencies are missing or field has no auto-generate config.
     */
    function getAutoGeneratedValue(key: string): string | null {
        const field = fields.value.find((f) => f.key === key);
        if (!field?.autoGenerate) return null;

        const config = field.autoGenerate as AutoGenerateConfig;
        const data = getFormData(formData.value);

        // Template mode
        if (config.template) {
            return applyTemplate(config.template, data);
        }

        // Named generator mode
        if (config.generator) {
            const generatorFn = getGenerator(config.generator);
            if (!generatorFn) {
                console.warn(
                    `[useAutoGenerate] Generator "${config.generator}" not registered`,
                );
                return null;
            }

            // Collect watched field values
            const watchedValues: Record<string, unknown> = {};
            for (const watchKey of config.watch) {
                watchedValues[watchKey] = getNestedValue(data, watchKey);
            }

            return generatorFn(watchedValues, config.params ?? {});
        }

        return null;
    }

    return {
        isManuallyEdited,
        markManualEdit,
        resetAutoGenerate,
        hasAutoGenerate,
        getAutoGeneratedValue,
        autoGenerateFields,
    };
}
